import * as React from 'react';

import useLearnerObjectUpdateCurrentOccupation from 'bundles/onboarding-common/hooks/useLearnerObjectUpdateCurrentOccupation';
import CurrentRoleEditor from 'bundles/personalization-settings/components/CurrentRoleEditor';
import SettingRow from 'bundles/personalization-settings/components/SettingRow';
import { usePersonalizationSettingsContext } from 'bundles/personalization-settings/contexts/PersonalizationSettingsContext';
import type { CurrentRoleValue } from 'bundles/personalization-settings/hooks/useCurrentRoleEditor';
import { useTrackPersonalizationSelectChange } from 'bundles/personalization-settings/hooks/usePersonalizationEventTracking';
import useResolvedCurrentRole from 'bundles/personalization-settings/hooks/useResolvedCurrentRole';
import useSettingSave from 'bundles/personalization-settings/hooks/useSettingSave';

export type CurrentRoleSettingItemProps = {
  label: string;
};

const LABEL_ID = 'current-role-label';

const CurrentRoleSettingItem = ({ label }: CurrentRoleSettingItemProps) => {
  const { values, loading: readLoading, setFieldValue } = usePersonalizationSettingsContext();
  const { updateCurrentOccupation } = useLearnerObjectUpdateCurrentOccupation();
  const trackSelectChange = useTrackPersonalizationSelectChange();

  const {
    value: seededValue,
    availableOccupations,
    loading: catalogLoading,
  } = useResolvedCurrentRole(values.currentRole);

  const {
    value: editorValue,
    notificationType,
    onChange: handleSave,
  } = useSettingSave(
    seededValue,
    async (value) => {
      let payload: Record<string, string>;
      let patchedValue: Record<string, string> | null;
      if (value && 'id' in value) {
        payload = { id: value.id };
        patchedValue = { id: value.id };
      } else if (value) {
        payload = { otherText: value.name };
        patchedValue = { name: value.name };
      } else {
        payload = {};
        patchedValue = null;
      }
      const result = await updateCurrentOccupation(payload);
      if (result.success) {
        setFieldValue('currentRole', patchedValue);
      }
      return result;
    },
    'currentRole'
  );

  const handleChange = (value: CurrentRoleValue) => {
    handleSave(value);
    trackSelectChange('current_role', value ? [value.name] : []);
  };

  return (
    <SettingRow
      label={label}
      labelId={LABEL_ID}
      notificationType={notificationType}
      loading={readLoading || catalogLoading}
    >
      <CurrentRoleEditor
        labelId={LABEL_ID}
        value={editorValue}
        onChange={handleChange}
        availableOccupations={availableOccupations}
      />
    </SettingRow>
  );
};

export default CurrentRoleSettingItem;
